import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'
import { styled } from '@mui/system'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Modal, Typography } from '@mui/material'
import { generateUtilityClasses } from '@mui/material'
import { useSelector } from 'react-redux'
import { useField, useForm, useFormState } from 'react-final-form'
import {
  TFullResponse,
  TSelectedSearchAccount,
  UspAccountingChartOfAccountCurrentView,
  UspAccountingReturnControlDataResult,
  UspListCarExpenseTypeResult,
} from '../../../../../models'
import DropdownFieldSearch from '../../../../../ui/DropdownFieldSearch'
import { AmountField, TextField } from '../../../../../ui'
import { getBranch } from '../../../../../redux/slices/userDropdownSlice'
import { formatNumber, parseToNumber } from '../../../../../utils/general'

const classes = generateUtilityClasses('form', ['root', 'row'])

const StyledBox = styled('form')(({ theme }) => ({
  [`&.${classes.root}`]: {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: theme.palette.background.main,
    padding: theme.spacing(3),
    minWidth: '350px',
  },

  [`& .${classes.row}`]: {
    margin: theme.spacing(1, 0, 2, 0),
  },
}))

type LimitFieldKey = 'UnpaidLien' | 'LienPaid'

interface Props {
  onSave: (newData: TFullResponse) => void
  open: boolean
  requestCarID: number | null
  requestDealID: number | null
  handleClose: Dispatch<SetStateAction<boolean>>
  getAccountingControlData: (e: { chartOfAccountId: string }) => void
  getListsCarsExpense: (e: { isAddingNewExpense: string }) => void
  accountingControlData: UspAccountingReturnControlDataResult
  chartOfAccountBillData: UspAccountingChartOfAccountCurrentView[]
  setRequestCarID: Dispatch<SetStateAction<null | number>>
  setRequestDealID: Dispatch<SetStateAction<null | number>>
  listsCarsExpenseData: UspListCarExpenseTypeResult[]
  lockFields: () => void
  addCheckIsSuccess: boolean
  errors: boolean
  setEditingItemIndex: Dispatch<SetStateAction<number | null>>
  setSelectedSearchAccount: Dispatch<SetStateAction<TSelectedSearchAccount | {}>>
  selectedSearchAccount: TSelectedSearchAccount
  isEdit: boolean
  setIsEdit: Dispatch<SetStateAction<boolean>>
  setModalDefaultValues: Dispatch<SetStateAction<TFullResponse | {}>>
  modalDefaultValues: any
}

const ChecksAddEditModal = ({
  open,
  handleClose,
  setRequestCarID,
  setRequestDealID,
  chartOfAccountBillData,
  getAccountingControlData,
  accountingControlData,
  getListsCarsExpense,
  listsCarsExpenseData,
  lockFields,
  requestDealID,
  requestCarID,
  onSave,
  errors,
  setEditingItemIndex,
  addCheckIsSuccess,
  setSelectedSearchAccount,
  selectedSearchAccount,
  isEdit,
  setIsEdit,
  setModalDefaultValues,
  modalDefaultValues,
}: Props) => {
  const form = useForm()
  const { values } = useFormState()
  const [searchResults, setSearchResults] = useState<any[]>([])
  const [filteredExpenseTypes, setFilteredExpenseTypes] = useState<UspListCarExpenseTypeResult[]>([])
  const branchData = useSelector(getBranch)
  const [currentControlDetails, setCurrentControlDetails] = useState<string | null>(null)
  const [isControlled, setIsControlled] = useState(false)
  const [controlSubType, setControlSubType] = useState<string | null>(null)
  const {
    input: { value: chartOfAccountBillValue },
  } = useField('ChartOfAccountBill')
  const {
    input: { value: searchValue },
  } = useField('Search')

  const {
    input: { value: selectedExpenseType },
  } = useField('ExpenseType')

  const handleResetFormValues = () => {
    form.change('ChartOfAccountBill', null)
    form.change('searchResults', null)
    form.change('ExpenseType', null)
    form.change('IsFloorplan', false)
    form.change('Branch', null)
    form.change('Amount', null)
    form.change('Note', '')
    if (setEditingItemIndex) {
      setEditingItemIndex(null)
    }
    if (setSelectedSearchAccount) {
      setSelectedSearchAccount({})
    }
    setRequestDealID(null)
    setRequestCarID(null)
    setControlSubType(null)
    setIsControlled(false)
    setSearchResults([])
    setFilteredExpenseTypes([])
    setCurrentControlDetails(null)
  }

  const handleSelection = (selectedItem: number) => {
    const foundItem = searchResults.find(
      (item) => item?.CarID === selectedItem || item?.DealID === selectedItem || item?.DealTradeID === selectedItem,
    )

    if (setSelectedSearchAccount) {
      setSelectedSearchAccount(foundItem)
    }
    const selectedAccount = chartOfAccountBillData?.find((item) => item?.ChartOfAccountID === chartOfAccountBillValue)

    if (!isEdit && controlSubType === 'Trade' && selectedSearchAccount) {
      form.change('Amount', selectedSearchAccount?.UnpaidLien?.toString())
    }

    if (selectedAccount && selectedItem) {
      if (selectedAccount?.ControlDetails === 'Account') {
        setRequestDealID(selectedItem)
      }
      if (selectedAccount?.ControlDetails === 'Inventory') {
        setRequestCarID(selectedItem)
      }
    } else {
      if (selectedAccount?.ControlDetails === 'Inventory') {
        setRequestCarID(selectedItem)
      }
      if (selectedAccount?.ControlDetails === 'Account') {
        setRequestDealID(selectedItem)
      }
    }
  }

  const determineOptionKeyAndValue = (results: any[]) => {
    const firstResult = results?.[0]
    if (firstResult?.DealID) {
      return { optionKey: 'DealID', optionValue: 'Customer' }
    } else if (firstResult?.CarID) {
      return { optionKey: 'CarID', optionValue: 'Vehicle' }
    } else {
      return { optionKey: 'DealTradeID', optionValue: 'TradeVehicle' }
    }
  }

  const { optionKey, optionValue } = useMemo(() => determineOptionKeyAndValue(searchResults), [searchResults])

  useEffect(() => {
    if (addCheckIsSuccess) {
      handleResetFormValues()
    }
  }, [addCheckIsSuccess])

  useEffect(() => {
    handleSelection(values?.searchResults)
  }, [values?.searchResults, selectedSearchAccount, controlSubType])

  useEffect(() => {
    if (!accountingControlData || !accountingControlData.ReturnData) return
    const returnData = JSON?.parse(accountingControlData?.ReturnData)
    const matchesSearch = (value: string) => value?.toLowerCase().includes(searchValue?.toLowerCase() || '')
    const filteredResults = returnData?.filter((item: any) => {
      return matchesSearch(item.Customer) || matchesSearch(item.Vehicle) || matchesSearch(item.TradeVehicle)
    })
    setSearchResults(filteredResults)
    const [firstResult] = returnData
    if (firstResult) {
      if (firstResult.BranchID && !isEdit) {
        form.change('Branch', firstResult.BranchID)
      }
    } else {
      setIsControlled(false)
    }
  }, [searchValue, accountingControlData, isControlled, selectedSearchAccount, isEdit])

  useEffect(() => {
    const selectedAccount = chartOfAccountBillData?.find((item) => item?.ChartOfAccountID === chartOfAccountBillValue)

    const {
      ControlDetails: controlDetails,
      ControlSubType: controlSubType,
      IsControlled: isControlledAccount,
      ChartOfAccountID: accountID,
    } = selectedAccount || {}

    setRequestCarID(null)
    setRequestDealID(null)
    setCurrentControlDetails(controlDetails || '')
    setControlSubType(controlSubType || '')

    setIsControlled(isControlledAccount || false)

    let filteredExpenses = listsCarsExpenseData // default
    if (controlSubType === 'Sold') {
      filteredExpenses = listsCarsExpenseData?.filter((item) => item?.IsAvailableForSoldInventory)
    } else if (controlSubType === 'Unsold') {
      filteredExpenses = listsCarsExpenseData?.filter((item) => item?.IsAvailableForUnsoldInventory)
    }

    setFilteredExpenseTypes(filteredExpenses)

    if (isControlledAccount || controlSubType === 'Trade') {
      getAccountingControlData({
        chartOfAccountId: accountID?.toString() as string,
      })
    }

    if (controlDetails === 'Inventory') {
      getListsCarsExpense({
        isAddingNewExpense: 'true',
      })
    }

    if (!isControlledAccount) {
      form.change('searchResults', [])
      form.change('ExpenseType', null)
      setSearchResults([]) // reset
    }
  }, [chartOfAccountBillValue, chartOfAccountBillData, listsCarsExpenseData, isControlled])

  const isExpenseTypePurchase = useMemo(() => {
    const selectedExpense = filteredExpenseTypes?.find((item) => item?.ExpenseType === selectedExpenseType)

    return selectedExpense?.ExpenseType === 'Purchase'
  }, [filteredExpenseTypes, selectedExpenseType])

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (isEdit) {
          handleResetFormValues()

          // extract Vendor and Payment from the current modalDefaultValues
          const { Vendor, Payment } = modalDefaultValues as any
          setModalDefaultValues({
            Vendor: Vendor || 'Miscellaneous',
            Payment: Payment,
          })
        }
        handleClose(true)
        setIsEdit(false)
      }}
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description'
    >
      <DialogTitle>Line Item</DialogTitle>

      <DialogContent>
        <Grid container spacing={2} py={1}>
          <Grid item xs={12}>
            <DropdownFieldSearch
              name='ChartOfAccountBill'
              label='Chart Of Account (Bill)'
              options={chartOfAccountBillData}
              optionKey={'ChartOfAccountID'}
              optionValue={'AccountNumberName'}
            />
          </Grid>
          {isControlled && (
            <Grid item xs={12}>
              <DropdownFieldSearch
                // customDropdownWidth={600}
                id='search-results'
                name='searchResults'
                label='Search Results'
                options={searchResults}
                optionKey={optionKey}
                optionValue={optionValue}
                getOptionLabel={(option: any) => option[optionKey] || ''}
                renderInput={(params: any) => <TextField {...params} label='Search Results' />}
                renderOption={(props: any, option: any) => (
                  <li {...props} key={optionKey}>
                    <Grid container justifyContent={'space-between'}>
                      <Grid item>
                        <Typography variant='body2'>{option.Customer || option.Vehicle}</Typography>
                        <Typography
                          variant='body2'
                          sx={{
                            color: 'primary.main',
                          }}
                        >
                          {option.StockNumber} | {option.BranchCode}
                        </Typography>
                      </Grid>
                      <Grid item sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center' }}>
                        <Typography variant='caption'>${formatNumber((option?.Balance as number) || (option?.InventoryCost as number))}</Typography>
                        <Typography
                          variant='body2'
                          sx={{
                            color: option?.AccountStatus === 'Active' ? 'green' : 'red',
                            fontWeight: option?.AccountStatus === 'Active' ? 'bold' : 'regular',
                          }}
                        >
                          {option.AccountStatus || option.CarStatus}
                        </Typography>
                      </Grid>
                    </Grid>
                  </li>
                )}
              />
            </Grid>
          )}

          {currentControlDetails === 'Inventory' && (
            <Grid container item xs={12}>
              {isControlled && (
                <Grid item xs={12}>
                  <DropdownFieldSearch
                    name='ExpenseType'
                    label='Expense Type'
                    options={filteredExpenseTypes}
                    optionKey='ExpenseType'
                    optionValue='ExpenseType'
                  />
                </Grid>
              )}

              {isExpenseTypePurchase && (
                <>
                  <Grid item xs={12}>
                    <DropdownFieldSearch
                      name='IsFloorplan'
                      label='Is Floorplan?'
                      options={[
                        { key: true, value: 'Yes' },
                        { key: false, value: 'No' },
                      ]}
                      optionKey='key'
                      optionValue='value'
                    />
                  </Grid>
                </>
              )}
            </Grid>
          )}
          <Grid item xs={12}>
            <DropdownFieldSearch
              disabled={isControlled}
              name='Branch'
              label='Branch'
              options={branchData}
              optionKey='BranchID'
              optionValue='Branch'
            />
          </Grid>
          <Grid item xs={12}>
            <AmountField
              variant='standard'
              validate={() => {
                if (controlSubType !== 'Trade') return
                const amountValue = parseToNumber(values.Amount?.toString() as string)
                let limitValue: number | undefined
                let limitField: LimitFieldKey | undefined
                if (limitField) {
                  limitValue = parseToNumber(selectedSearchAccount?.[limitField]?.toString() as string)
                }
                if (limitValue !== undefined && amountValue > limitValue) {
                  return `Amount cannot be greater than ${formatNumber(limitValue.toString())}`
                }
              }}
              name='Amount'
              label='Amount'
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextField name='Note' label='Comments (Optional)' fullWidth multiline autoComplete='off' />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          variant='contained'
          // color='primary'
          type='submit'
          disabled={errors}
          onClick={() => {
            if (lockFields) {
              lockFields()
            }
            const { values } = form.getState()
            const { Payment, Note, Branch, ExpenseType, ChartOfAccountBill, searchResults, IsFloorplan, Amount, invoiceTo } = values
            const matchedItem = chartOfAccountBillData?.find((item) => item?.ChartOfAccountID === ChartOfAccountBill)
            const matchedBranch = branchData?.find((item) => item?.BranchID === Branch)

            const coaAccountNumberValue = matchedItem?.AccountNumberName
            const matchedBranchName = matchedBranch?.Branch

            const newData: any = {
              Payment,
              Vendor: invoiceTo?.ID || null,
              Description: Note,
              BranchCode: Branch,
              CarExpenseType: ExpenseType,
              AccountNumberName: ChartOfAccountBill,
              BranchID: Branch,
              SearchResult: searchResults,
              IsFloorplan,
              BillAmount: parseToNumber(Amount),
              AccountNumberNameValue: coaAccountNumberValue,
              SelectedSearchAccount: selectedSearchAccount,
              BranchName: matchedBranchName,
              CarID: requestCarID,
              DealID: requestDealID,
              StockNumber: selectedSearchAccount?.StockNumber,
              SelectedCoaItem: matchedItem,
              ID: Math.floor(Math.random() * 100),
            }

            onSave(newData)
            handleClose(true)
            handleResetFormValues()
            // Always reset these fields
            const fieldsToReset = ['Payment', 'Branch', 'ChartOfAccountBill', 'Amount']

            // Conditionally add 'Vendor' if it's present
            if (values.Payment === 'Vendor') {
              fieldsToReset.push('invoiceTo')
            }
            fieldsToReset?.forEach((field) => form.resetFieldState(field))
          }}
        >
          {isEdit ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ChecksAddEditModal
